// Pure helpers for the Vietnam deposit on the CRM booking-detail screen. Why the
// section exists, when it is absent, where its amounts come from and every rule below are in
// docs/merge-audit-main-svn-to-master.md, under the Vietnam deposit.
import { formatCurrencyAmount } from './money';
import { formatValue } from './string';

// The booking statuses at which a unit is reserved, i.e. from which a deposit can exist.
export const DEPOSIT_VISIBLE_SR_STATUSES = [
  'paid_unverified',
  'paid_verified',
  'approved',
  'pending_cancellation',
  'pending_refund',
  'refunded',
  'refund_declined',
];

export function hasReservedUnit(opportunity) {
  if (!opportunity) return false;
  return DEPOSIT_VISIBLE_SR_STATUSES.includes(opportunity.sr_status);
}

// `booking_fee_payments.status` → i18n key; `paid` means approved by sales, not merely settled.
export const DEPOSIT_STATUS_LABEL_KEYS = {
  pending_verification: 'component.label.deposit_pending_approval',
  paid: 'component.label.deposit_approved',
  rejected: 'component.label.deposit_rejected',
  cancelled: 'component.label.deposit_cancelled',
  deduction: 'component.label.deposit_deduction',
};

export const DEPOSIT_STATUS_LABEL_DEFAULTS = {
  pending_verification: 'Pending approval',
  paid: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  deduction: 'Deduction',
};

export const DEPOSIT_STATUS_APPROVED = 'paid';

export function depositStatusLabel(t, status) {
  if (!status) return '-';
  const key = DEPOSIT_STATUS_LABEL_KEYS[status];
  if (!key) return formatValue(status);
  return t(key, DEPOSIT_STATUS_LABEL_DEFAULTS[status]);
}

export function isDepositApproved(payment) {
  return Boolean(payment) && payment.status === DEPOSIT_STATUS_APPROVED;
}

const WORD_LIKE = /^[a-z][a-z_]*$/;

// Label for `payment_method_type_id`: a word is translated, an opaque id shows as "-".
export function depositPaymentMethodLabel(t, methodTypeId) {
  if (methodTypeId == null || methodTypeId === '') return '-';
  const raw = String(methodTypeId);
  if (!WORD_LIKE.test(raw)) return '-';
  return t(`component.label.payment_method.${raw}`, formatValue(raw));
}

export function selectDepositPayments(deposit) {
  if (!deposit || !Array.isArray(deposit.payments)) return [];
  return deposit.payments.filter(Boolean);
}

// Amount, paid and outstanding as text; the server's own `*_text` strings win over arithmetic.
export function depositTotals(deposit, currencyCode = 'VND') {
  if (!deposit) return { amount: '-', paid: '-', outstanding: '-' };
  return {
    amount: deposit.payment_amount_text
      || formatCurrencyAmount(deposit.payment_amount, currencyCode),
    paid: deposit.total_paid_text
      || formatCurrencyAmount(deposit.total_paid, currencyCode),
    outstanding: deposit.outstanding_text
      || formatCurrencyAmount(deposit.outstanding, currencyCode),
  };
}

// The booking fee and the deposit side by side, so a CRM user can tell the two monies apart.
export function selectTwoMonies(tokens, deposit) {
  const latestToken = (Array.isArray(tokens) ? tokens : []).find(Boolean) || null;
  const feeCurrency = latestToken?.currency_code || 'VND';
  const depositCurrency = deposit?.currency_code || feeCurrency;

  return {
    bookingFee: {
      amount: formatCurrencyAmount(latestToken?.amount_minor, feeCurrency),
      currencyCode: feeCurrency,
    },
    deposit: {
      ...depositTotals(deposit, depositCurrency),
      currencyCode: depositCurrency,
    },
  };
}

export function hasDepositToShow(deposit) {
  if (!deposit) return false;
  if (selectDepositPayments(deposit).length > 0) return true;
  if (deposit.payment_amount_text) return true;
  return deposit.payment_amount != null && !Number.isNaN(Number(deposit.payment_amount));
}

// `receipt_url` is the signal that a receipt exists; its value is never followed by the browser.
export function hasDepositReceipt(payment) {
  return isDepositApproved(payment) && Boolean(payment.receipt_url);
}

export function depositReceiptBookingId(payment, deposit) {
  return payment?.booking_id || deposit?.booking_id || '';
}

export const DEPOSIT_RECEIPT_DEFAULT_CONTENT_TYPE = 'application/pdf';

// A browser-openable address for a receipt; a `url` that is not a `data:` URL is discarded.
export function depositReceiptDataUrl(receipt) {
  if (!receipt) return '';
  if (typeof receipt.url === 'string' && receipt.url.startsWith('data:')) return receipt.url;
  if (!receipt.file_content) return '';
  const contentType = receipt.content_type || DEPOSIT_RECEIPT_DEFAULT_CONTENT_TYPE;
  return `data:${contentType};base64,${receipt.file_content}`;
}

// data:<content-type>;base64,<body> — the shape `depositReceiptDataUrl` produces. The type is
// matched up to the `;base64,` rather than to the first `;` so a content type that carries a
// parameter ("application/pdf; charset=binary") is still read as a receipt and not as a
// malformed one; `Blob` takes the parameter in its stride. Neither `;` nor `,` occurs in base64,
// so the body cannot be mistaken for part of the type.
const RECEIPT_DATA_URL_RE = /^data:([^,]*);base64,(.*)$/;

/**
 * The receipt's bytes, content type and name, whichever of the proxy's two shapes it answered
 * in: a ready-made `data:` URL in `url`, or `file_content` plus `content_type`.
 *
 * Parses what `depositReceiptDataUrl` builds rather than reading the response a second time, so
 * the two shapes stay known in exactly one place. Returns null in the same cases that helper
 * returns '' for — no receipt, no bytes, and the `/bms/` URL it discards because following it
 * needs the `X-Token` header the browser does not hold.
 *
 * The caller wants the parts and not the `data:` URL because Chrome will not navigate a tab to
 * one; `base64ToObjectUrl` turns these into the `blob:` URL it will navigate to.
 *
 * @param {{ url?: string, file_content?: string, content_type?: string,
 *           file_name?: string }} receipt
 * @returns {{ base64: string, contentType: string, fileName: string }|null}
 */
export function depositReceiptFile(receipt) {
  const dataUrl = depositReceiptDataUrl(receipt);
  if (!dataUrl) return null;
  const match = RECEIPT_DATA_URL_RE.exec(dataUrl);
  if (!match || !match[2]) return null;
  return {
    base64: match[2],
    contentType: match[1] || DEPOSIT_RECEIPT_DEFAULT_CONTENT_TYPE,
    fileName: receipt.file_name || '',
  };
}

// External (agency) branch users may not see the deposit — book-api's `vnBlocksExternalAgent`.
export function isExternalAgencyUser(user) {
  const isExternal = user?.companyBranch?.isExternal;
  if (typeof isExternal === 'string') return isExternal.toLowerCase() === 'true';
  return isExternal === true;
}
